import Vuex from "vuex";
import Vue from "vue";
import { graphqlClient } from "@/utils/graphql";
import gql from "graphql-tag";
import { ProductInfo, ProductInfoResult, QUERY_PRODUCT_INFO } from "@/models/queries/ProductInfoQuery";
import { ProductOverviewQueryResult, QUERY_PRODUCT_OVERVIEW } from "@/models/queries/ProductOverviewQuery";
import { MUTATION_ADD_PRODUCT } from "@/models/mutations/AddProductMutation";
import { QUERY_PRODUCT_DETAIL } from "@/models/queries/ProductDetailQuery";
import { MUTATION_IMPORT_PRODUCTS } from "@/models/mutations/ImportProducts";
import { PRODUCT_INFO_SEARCH_QUERY } from "@/models/queries/ProductInfoSearchQuery";
import { MUTATION_IMPORT_PRODUCT_TEMPLATE_DATA } from "@/models/mutations/ImportProductTemplateData";
import { DELETE_PRODUCT_MUTATION } from "@/models/mutations/DeleteProduct";
import { EDIT_PRODUCT_QUERY } from "@/models/queries/EditProductQuery";
import { EDIT_PRODUCT_MUTATION } from "@/models/mutations/EditProductMutation";
import { VALIDATE_IMPORT_PRODUCTS_MUTATION } from "@/models/mutations/ValidateImportProducts";
import { VALIDATE_PRODUCT_TEMPLATE_DATA_MUTATION } from "@/models/mutations/ValidateProductTemplateData";
import * as _ from "lodash";

Vue.use(Vuex);
export interface ProductInfoState {
  fields: ProductInfo[];
  structure: ProductInfo[][];
}

const productStore = {
  namespaced: true,
  state: {
    products: [],
    productInfo: {},
    fetching: false,
    hasError: false
  },
  mutations: {
    BEGIN(state) {
      state.fetching = true;
    },
    END(state) {
      state.fetching = false;
    },
    ERROR(state, message) {
      state.fetching = false;
      state.hasError = message;
    },
    FETCH(state, products) {
      state.products = products;
    },
    FETCH_INFO(state, info) {
      state.productInfo = info;
    }
  },
  actions: {
    fetchAsync() {
      return graphqlClient.query({
        query: gql`
          ${QUERY_PRODUCT_OVERVIEW}
        `
      });
    },
    fetchSingle({}, id) {
      return new Promise(async (resolve, reject) => {
        try {
          const response = await graphqlClient.query({
            query: gql`
              ${EDIT_PRODUCT_QUERY}
            `,
            variables: { productId: id }
          });

          const productResponse = response.data.product;
          const properties = {};
          productResponse.properties.stringValue.forEach(prop => {
            properties[prop.propertyType] = prop;
          });
          productResponse.properties.singleValue.forEach(prop => {
            properties[prop.propertyType] = prop;
          });
          productResponse.properties.multpileValues.forEach(prop => {
            properties[prop.propertyType] = prop.values;
          });

          const product = {
            ...productResponse,
            properties
          };

          resolve([response.data.templates, response.data.characteristics, product]);
        } catch (e) {
          reject(e);
        }
      });
    },
    async fetchInfo({ commit }) {
      const productInfoResult = await graphqlClient.query<ProductInfoResult>({
        query: QUERY_PRODUCT_INFO
      });

      const info: ProductInfoState = {
        fields: productInfoResult.data.productInfo,
        structure: []
      };

      info.fields.forEach((field, index) => {
        if (index === 0) {
          info.structure.push([field]);
        } else {
          if (index % 2 === 0) {
            info.structure.push([field]);
          } else {
            info.structure.slice(-1)[0].push(field);
          }
        }
      });

      commit("FETCH_INFO", info);
    },
    async infoSearch({ commit }, id) {
      return graphqlClient.query({
        query: gql`
          ${PRODUCT_INFO_SEARCH_QUERY}
        `,
        variables: { id: id }
      });
    },
    async fetchDetail({ commit }, id) {
      const response = await graphqlClient.query({
        query: gql`
          ${QUERY_PRODUCT_DETAIL}
        `,
        variables: { productId: id }
      });

      const productResponse = response.data.product;
      const properties = {};
      productResponse.properties.stringValue.forEach(prop => {
        properties[prop.propertyType] = prop;
      });
      productResponse.properties.singleValue.forEach(prop => {
        properties[prop.propertyType] = prop;
      });
      productResponse.properties.multpileValues.forEach(prop => {
        properties[prop.propertyType] = prop.values;
      });

      return {
        ...productResponse,
        properties
      };
    },
    save({}, input) {
      return new Promise((resolve, reject) => {
        graphqlClient
          .mutate({
            mutation: gql`
              ${MUTATION_ADD_PRODUCT}
            `,
            variables: { input: input }
          })
          .then(response => {
            resolve();
          })
          .catch(e => reject(e));
      });
    },
    update({}, variables) {
      return graphqlClient.mutate({
        mutation: gql(EDIT_PRODUCT_MUTATION),
        variables
      });
    },
    import({}, productsRaw) {
      const numberRegex = /^-?\d+$/;
      const products = productsRaw.map(p => {
        const propertyElements = Object.keys(p).filter(key => !numberRegex.test(key)); // product properties
        const shortcutsElements = Object.keys(p).filter(key => numberRegex.test(key)); // key is a characteristic id
        return {
          properties: propertyElements.map(key => {
            return {
              key: key,
              values: p[key]
            };
          }),
          productCharacteristicsCustomShortcuts: shortcutsElements.map(key => ({
            characteristicId: parseInt(key, 10),
            customShortcut: p[key][0]
          }))
        };
      });

      return new Promise((resolve, reject) => {
        graphqlClient
          .mutate({
            mutation: gql`
              ${MUTATION_IMPORT_PRODUCTS}
            `,
            variables: {
              products: products
            }
          })
          .then(response => {
            console.log(response);
            resolve();
          })
          .catch(e => reject(e));
      });
    },
    importProductTemplateData({}, data) {
      return graphqlClient.mutate({
        mutation: gql`
          ${MUTATION_IMPORT_PRODUCT_TEMPLATE_DATA}
        `,
        variables: {
          productData: data
        }
      });
    },
    validateProductImport({}, productsRaw) {
      const numberRegex = /^-?\d+$/;
      const products = productsRaw.map(p => {
        const propertyElements = Object.keys(p).filter(key => !numberRegex.test(key)); // product properties
        const shortcutsElements = Object.keys(p).filter(key => numberRegex.test(key)); // key is a characteristic id
        return {
          properties: propertyElements.map(key => {
            return {
              key: key,
              values: p[key]
            };
          }),
          productCharacteristicsCustomShortcuts: shortcutsElements.map(key => ({
            characteristicId: parseInt(key, 10),
            customShortcut: p[key][0]
          }))
        };
      });

      return new Promise((resolve, reject) => {
        graphqlClient
          .mutate({
            mutation: gql`
              ${VALIDATE_IMPORT_PRODUCTS_MUTATION}
            `,
            variables: {
              products: products
            }
          })
          .then(response => {
            console.log(response);
            resolve(response.data.validateImportProducts);
          })
          .catch(e => reject(e));
      });
    },
    validateProductTemplateImport({}, data) {
      return graphqlClient.mutate({
        mutation: gql`
          ${VALIDATE_PRODUCT_TEMPLATE_DATA_MUTATION}
        `,
        variables: {
          productData: data
        }
      });
    }
  }
};

export default productStore;
